import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaMedal,
  FaSearch,
  FaShieldAlt,
  FaSpinner,
  FaTable,
  FaTrophy,
  FaUsers,
} from 'react-icons/fa';
import api from '../api/axios';
import { AppContext } from '../context/AppContext';

const initialFilter = {
  search: '',
  competition_type: 'Kyorugi',
  type: '',
  division: '',
  gender: '',
  weight_class: '',
  poomsae_category: '',
};

const sortOptions = [
  { value: 'name_asc', label: 'Nama A-Z' },
  { value: 'team_asc', label: 'Kontingen A-Z' },
  { value: 'division_asc', label: 'Divisi A-Z' },
  { value: 'type_asc', label: 'Kategori A-Z' },
];

const getTeamName = (athlete) => athlete.team?.name || athlete.Team?.name || '-';

const PublicCheckData = () => {
  const { masterData } = useContext(AppContext);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState(initialFilter);
  const [sortBy, setSortBy] = useState('name_asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getWeightOptions = () => {
    const weightClasses = masterData?.weight_classes || {};
    const gender = filter.gender || 'Lelaki';

    if (filter.division) {
      let key = filter.division;
      if (!weightClasses[key] && key.includes('Pracadet')) key = 'Pracadet';
      return weightClasses[key]?.[gender] || [];
    }

    return Object.values(weightClasses)
      .flatMap((byGender) => byGender?.[gender] || [])
      .filter((item, index, array) => item && array.indexOf(item) === index);
  };

  useEffect(() => {
    const fetchPublicData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filter.search) params.append('search', filter.search);
        if (filter.competition_type) params.append('competition_type', filter.competition_type);
        if (filter.type) params.append('type', filter.type);
        if (filter.division) params.append('division', filter.division);
        if (filter.gender) params.append('gender', filter.gender);

        const response = await api.get(`/athletes/public-list?${params.toString()}`);
        const rows = Array.isArray(response.data) ? response.data : [];

        const filteredRows = rows.filter((athlete) => {
          if (filter.competition_type === 'Kyorugi' && filter.weight_class) {
            return athlete.weight_class === filter.weight_class;
          }

          if (filter.competition_type === 'Poomsae' && filter.poomsae_category) {
            return athlete.poomsae_category === filter.poomsae_category;
          }

          return true;
        });

        setAthletes(filteredRows);
      } catch (error) {
        console.error('Error fetching public data:', error);
        setAthletes([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchPublicData, 450);
    return () => clearTimeout(timeoutId);
  }, [filter]);

  const sortedAthletes = useMemo(() => {
    return [...athletes].sort((a, b) => {
      if (sortBy === 'team_asc') return getTeamName(a).localeCompare(getTeamName(b));
      if (sortBy === 'division_asc') return String(a.division || '').localeCompare(String(b.division || ''));
      if (sortBy === 'type_asc') return String(a.type || '').localeCompare(String(b.type || ''));
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [athletes, sortBy]);

  const totalPages = Math.max(1, Math.ceil(sortedAthletes.length / pageSize));
  const visibleAthletes = sortedAthletes.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setPage(1);
    setFilter((current) => {
      const next = { ...current, [name]: value };

      if (name === 'competition_type') {
        next.weight_class = '';
        next.poomsae_category = '';
      }

      if (name === 'division' || name === 'gender') {
        next.weight_class = '';
      }

      return next;
    });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const paginationLabel = sortedAthletes.length
    ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, sortedAthletes.length)} dari ${sortedAthletes.length}`
    : '0 data';

  return (
    <div className="sulteng-check">
      <section className="sulteng-check__hero">
        <div className="sulteng-check__pattern" />
        <div className="container position-relative">
          <span className="sulteng-check__badge">
            <FaShieldAlt /> Validasi Publik
          </span>
          <h1>Cek Data Peserta</h1>
          <p>Cari dan validasi data atlet yang telah terdaftar secara resmi pada sistem kejuaraan.</p>
        </div>
      </section>

      <section className="sulteng-check__content">
        <div className="container">
          <div className="sulteng-filter-card">
            <div className="sulteng-filter-card__title">
              <span><FaFilter /></span>
              <div>
                <small>Filter Otomatis</small>
                <h2>Data Atlet</h2>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">Cari Nama Atlet</label>
                <div className="input-group sulteng-search-group">
                  <span className="input-group-text"><FaSearch /></span>
                  <input
                    type="text"
                    className="form-control"
                    name="search"
                    value={filter.search}
                    onChange={handleFilterChange}
                    placeholder="Ketik nama atlet..."
                  />
                </div>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Nomor</label>
                <select className="form-select" name="competition_type" value={filter.competition_type} onChange={handleFilterChange}>
                  <option value="Kyorugi">Kyorugi</option>
                  <option value="Poomsae">Poomsae</option>
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Jenis Kategori</label>
                <select className="form-select" name="type" value={filter.type} onChange={handleFilterChange}>
                  <option value="">Semua</option>
                  <option value="Prestasi">Prestasi</option>
                  <option value="Festival">Festival</option>
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Divisi</label>
                <select className="form-select" name="division" value={filter.division} onChange={handleFilterChange}>
                  <option value="">Semua Divisi</option>
                  {masterData?.divisions?.map((division) => (
                    <option key={division.name} value={division.name}>{division.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Gender</label>
                <select className="form-select" name="gender" value={filter.gender} onChange={handleFilterChange}>
                  <option value="">Semua Gender</option>
                  <option value="Lelaki">Lelaki</option>
                  <option value="Perempuan">Perempuan</option>
                  <option value="Campuran">Campuran</option>
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <label className="form-label">Kategori / Kelas</label>
                {filter.competition_type === 'Kyorugi' ? (
                  <select className="form-select" name="weight_class" value={filter.weight_class} onChange={handleFilterChange}>
                    <option value="">Semua Kelas</option>
                    {getWeightOptions().map((weightClass) => (
                      <option key={weightClass} value={weightClass}>{weightClass}</option>
                    ))}
                  </select>
                ) : (
                  <select className="form-select" name="poomsae_category" value={filter.poomsae_category} onChange={handleFilterChange}>
                    <option value="">Semua Kategori</option>
                    <option value="Individual">Individual</option>
                    <option value="Pair">Pair</option>
                    <option value="Team">Team</option>
                  </select>
                )}
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <label className="form-label">Urutkan</label>
                <select className="form-select" value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <label className="form-label">Tampilkan</label>
                <select className="form-select" value={pageSize} onChange={handlePageSizeChange}>
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>{size} baris</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="sulteng-result-card">
            <div className="sulteng-result-card__header">
              <div>
                <span><FaTrophy /> Hasil Pencarian</span>
                <h2>{sortedAthletes.length} Atlet</h2>
              </div>
              <div className="sulteng-result-card__meta">
                {loading ? (
                  <span><FaSpinner className="sulteng-spin" /> Memuat</span>
                ) : (
                  <span><FaTable /> {paginationLabel}</span>
                )}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table sulteng-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Nama Atlet</th>
                    <th>Kontingen / Tim</th>
                    <th>Divisi</th>
                    <th>Nomor</th>
                    <th>Sabuk</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-5">
                        <div className="sulteng-table-loader">
                          <div className="spinner-border text-success" role="status" />
                          <span>Memuat data atlet...</span>
                        </div>
                      </td>
                    </tr>
                  ) : visibleAthletes.length > 0 ? (
                    visibleAthletes.map((athlete) => (
                      <tr key={athlete.uuid || athlete.id}>
                        <td>
                          <div className="sulteng-athlete-name">
                            <span><FaUsers /></span>
                            <div>
                              <strong>{athlete.name}</strong>
                              <small>{athlete.gender || '-'}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <strong className="sulteng-team-name">{getTeamName(athlete)}</strong>
                        </td>
                        <td>
                          <span className="sulteng-pill green">{athlete.division || '-'}</span>
                        </td>
                        <td>
                          <div className="sulteng-category-cell">
                            <span className="sulteng-pill orange">{athlete.type || '-'}</span>
                            <strong>{athlete.competition_type}</strong>
                            <small>
                              {athlete.competition_type === 'Kyorugi'
                                ? athlete.weight_class || '-'
                                : athlete.poomsae_category || '-'}
                            </small>
                          </div>
                        </td>
                        <td>
                          <span className="sulteng-pill neutral">{athlete.belt || '-'}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-5">
                        <div className="sulteng-empty-result">
                          <FaMedal />
                          <h3>Tidak ada data yang cocok</h3>
                          <p>Ubah filter untuk melihat data peserta lainnya.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="sulteng-pagination">
              <span>{paginationLabel}</span>
              <div className="d-flex gap-2">
                <button type="button" className="btn" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>
                  <FaChevronLeft /> Sebelumnya
                </button>
                <button type="button" className="btn" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>
                  Berikutnya <FaChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .sulteng-check {
          min-height: 100vh;
          background: #f8fafc;
          color: #0f172a;
        }

        .sulteng-check__hero {
          position: relative;
          overflow: hidden;
          padding: 8.5rem 0 6rem;
          color: #ffffff;
          text-align: center;
          background: linear-gradient(135deg, #14532d, #052e16);
        }

        .sulteng-check__pattern {
          position: absolute;
          inset: 0;
          opacity: 0.08;
          background-image: radial-gradient(#facc15 10%, transparent 10%), radial-gradient(#ffffff 10%, transparent 10%);
          background-size: 42px 42px;
          background-position: 0 0, 21px 21px;
        }

        .sulteng-check__badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding: 0.62rem 0.95rem;
          border: 1px solid rgba(250, 204, 21, 0.3);
          border-radius: 999px;
          color: #facc15;
          background: rgba(255, 255, 255, 0.1);
          font-size: 0.78rem;
          font-weight: 900;
          text-transform: uppercase;
          backdrop-filter: blur(12px);
        }

        .sulteng-check__hero h1 {
          margin: 0 auto 1rem;
          font-size: 3rem;
          font-weight: 900;
          line-height: 1.05;
          text-transform: uppercase;
        }

        .sulteng-check__hero p {
          max-width: 700px;
          margin: 0 auto;
          color: #bbf7d0;
          font-weight: 650;
          line-height: 1.75;
        }

        .sulteng-check__content {
          margin-top: -3rem;
          padding-bottom: 5rem;
          position: relative;
        }

        .sulteng-filter-card,
        .sulteng-result-card {
          border: 1px solid rgba(22, 101, 52, 0.1);
          border-radius: 26px;
          background: rgba(255, 255, 255, 0.96);
          box-shadow: 0 22px 50px rgba(15, 23, 42, 0.09);
          backdrop-filter: blur(14px);
        }

        .sulteng-filter-card {
          padding: 1.5rem;
          margin-bottom: 1.25rem;
        }

        .sulteng-filter-card__title {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.25rem;
        }

        .sulteng-filter-card__title > span {
          width: 54px;
          height: 54px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          border-radius: 18px;
          color: #14532d;
          background: #facc15;
          font-size: 1.25rem;
        }

        .sulteng-filter-card__title small {
          display: block;
          color: #ea580c;
          font-size: 0.72rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-filter-card__title h2 {
          margin: 0;
          color: #14532d;
          font-size: 1.6rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-filter-card .form-label {
          color: #475569;
          font-size: 0.75rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-filter-card .form-control,
        .sulteng-filter-card .form-select {
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          color: #0f172a;
          background-color: #f8fafc;
          font-weight: 700;
          min-height: 48px;
        }

        .sulteng-filter-card .form-control:focus,
        .sulteng-filter-card .form-select:focus {
          border-color: #22c55e;
          box-shadow: 0 0 0 0.25rem rgba(34, 197, 94, 0.14);
        }

        .sulteng-search-group .input-group-text {
          border: 1px solid #e2e8f0;
          border-right: 0;
          border-radius: 16px 0 0 16px;
          color: #166534;
          background: #f0fdf4;
        }

        .sulteng-search-group .form-control {
          border-left: 0;
          border-radius: 0 16px 16px 0;
        }

        .sulteng-result-card {
          overflow: hidden;
        }

        .sulteng-result-card__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem 1.5rem;
          color: #ffffff;
          background: linear-gradient(135deg, #14532d, #166534);
        }

        .sulteng-result-card__header span,
        .sulteng-result-card__meta span {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #facc15;
          font-size: 0.78rem;
          font-weight: 900;
          text-transform: uppercase;
        }

        .sulteng-result-card__header h2 {
          margin: 0.25rem 0 0;
          font-size: 1.65rem;
          font-weight: 900;
        }

        .sulteng-result-card__meta {
          padding: 0.7rem 0.9rem;
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.08);
        }

        .sulteng-table {
          min-width: 840px;
        }

        .sulteng-table thead th {
          color: #475569;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.76rem;
          font-weight: 900;
          text-transform: uppercase;
          padding: 1rem 1.25rem;
        }

        .sulteng-table tbody td {
          border-bottom: 1px solid #eef2f7;
          padding: 1rem 1.25rem;
        }

        .sulteng-table tbody tr:hover td {
          background: #f0fdf4;
        }

        .sulteng-athlete-name {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .sulteng-athlete-name > span {
          width: 42px;
          height: 42px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          border-radius: 14px;
          color: #166534;
          background: #dcfce7;
        }

        .sulteng-athlete-name strong,
        .sulteng-team-name {
          color: #0f172a;
          font-weight: 900;
        }

        .sulteng-athlete-name small {
          display: block;
          color: #64748b;
          font-size: 0.78rem;
          font-weight: 750;
        }

        .sulteng-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          padding: 0.42rem 0.68rem;
          font-size: 0.75rem;
          font-weight: 900;
          white-space: nowrap;
        }

        .sulteng-pill.green {
          color: #14532d;
          background: #dcfce7;
        }

        .sulteng-pill.orange {
          color: #9a3412;
          background: #ffedd5;
        }

        .sulteng-pill.neutral {
          color: #334155;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
        }

        .sulteng-category-cell {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.35rem;
        }

        .sulteng-category-cell strong {
          color: #14532d;
          font-weight: 900;
        }

        .sulteng-category-cell small {
          color: #64748b;
          font-weight: 700;
        }

        .sulteng-table-loader {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.8rem;
          color: #14532d;
          font-weight: 800;
        }

        .sulteng-empty-result {
          color: #64748b;
        }

        .sulteng-empty-result svg {
          color: rgba(100, 116, 139, 0.28);
          font-size: 3.5rem;
          margin-bottom: 1rem;
        }

        .sulteng-empty-result h3 {
          color: #334155;
          font-size: 1.2rem;
          font-weight: 900;
        }

        .sulteng-empty-result p {
          margin: 0;
          font-weight: 650;
        }

        .sulteng-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.5rem;
          background: #ffffff;
        }

        .sulteng-pagination > span {
          color: #475569;
          font-size: 0.86rem;
          font-weight: 800;
        }

        .sulteng-pagination .btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          border-radius: 999px;
          color: #14532d;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          font-weight: 900;
          padding: 0.65rem 0.9rem;
        }

        .sulteng-pagination .btn:hover:not(:disabled) {
          color: #ffffff;
          background: #166534;
        }

        .sulteng-pagination .btn:disabled {
          color: #94a3b8;
          background: #f1f5f9;
          border-color: #e2e8f0;
        }

        .sulteng-spin {
          animation: sultengSpin 0.9s linear infinite;
        }

        @keyframes sultengSpin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 767.98px) {
          .sulteng-check__hero {
            padding-top: 7.5rem;
          }

          .sulteng-check__hero h1 {
            font-size: 2.2rem;
          }

          .sulteng-result-card__header,
          .sulteng-pagination {
            flex-direction: column;
            align-items: flex-start;
          }

          .sulteng-pagination .d-flex {
            width: 100%;
          }

          .sulteng-pagination .btn {
            flex: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default PublicCheckData;
